import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { Schedule, SchedulingInput } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { Cadence, DaysOfTheWeek } from '@/enterprise/features/offerModal/types';
import { buildCustomScheduleSentenceFromFormData, validateCadence } from '@/enterprise/features/offerModal/utils';
import { useOfferBuilderContext } from '../OfferBuilderContext';

export interface EditScheduleModalProps {
  open: boolean;
  onClose: () => void;
}

export const defaultSchedule = {
  startDate: undefined,
  endDate: undefined,
  cadence: { type: 'daily' as const, value: DaysOfTheWeek },
};

const EditScheduleModal = (props: EditScheduleModalProps) => {
  const {
    dispatch,
    state: { selected, schedule: contextSchedule },
  } = useOfferBuilderContext();
  const { open, onClose } = props;
  const { t } = useTranslation();
  const [internalSchedule, setInternalSchedule] = useState<Schedule>(contextSchedule);
  const [errors, setErrors] = useState<{ cadence?: { message: string } }>({});

  const handleSave = () => {
    // Validate if updating to weekly schedule that at least one day is selected
    const scheduleValidation = validateCadence(internalSchedule.cadence, t);
    if (!scheduleValidation.valid) {
      setErrors({ cadence: { message: scheduleValidation.errorMessage || t('scheduling.selectDayOrReset') } });
      return;
    }

    dispatch({ type: 'SET_SCHEDULE', payload: internalSchedule });
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setInternalSchedule(contextSchedule);
    setErrors({});
    onClose();
  };

  const setStartDate = (date: string | undefined) => {
    setInternalSchedule((prev) => ({ ...prev, startDate: date }));
  };

  const setEndDate = (date: string | undefined) => {
    setInternalSchedule((prev) => ({ ...prev, endDate: date }));
  };

  const setCadence = (cadence: Cadence) => {
    setInternalSchedule((prev) => ({ ...prev, cadence }));
  };

  const scheduleSentence = buildCustomScheduleSentenceFromFormData({
    startDate: internalSchedule.startDate,
    endDate: internalSchedule.endDate,
    cadence: internalSchedule.cadence,
    t,
  });

  return (
    <Modal open={open} onClose={handleClose} className="sm:w-[750px] sm:min-w-0">
      <ModalTitleInverse>{t('builder.editScheduleModalTitle')}</ModalTitleInverse>
      <ModalContent>
        <div className="overflow-hidden rounded-md bg-canvas">
          <div className="flex items-center justify-between border-b-[0.5px] border-stroke p-4">
            <div>{t('scheduling.schedule')}</div>
            <div className="font-medium">{scheduleSentence}</div>
          </div>
          <div className="bg-blueGray-50">
            <SchedulingInput
              takerMarkets={selected}
              schedule={internalSchedule}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setCadence={setCadence}
              errors={errors}
            />
          </div>
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t('builder.saveSchedule')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default EditScheduleModal;
